import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TachographConnectivity, TachographRow, TachographStatus } from "../models/Tachograph";
import { TACHOGRAPH_PAGE_SIZES } from "./tachographGridDefaults";
import { TachographStatusIcon } from "./TachographStatusIcon";
import { I18nGrid } from "@/components/data/I18nGrid";

type Props = {
  rows: TachographRow[];
  loading: boolean;
};

const STATUS_COLUMN_WIDTH = 90;

/** The vehicle table shown by the status, inspection, settings, RCS and connector tabs. */
export function TachographVehicleGrid({ rows, loading }: Props) {
  const { t } = useTranslation();

  const columns = useMemo<GridColDef<TachographRow>[]>(() => {
    const statusColumn = (
      field: "status" | "downloadStatus" | "inspectionStatus" | "companyCardStatus",
    ): GridColDef<TachographRow> => ({
      field,
      headerName: t(`tachograph.columns.${field}`),
      width: STATUS_COLUMN_WIDTH,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<TachographRow, TachographStatus>) => (
        <TachographStatusIcon
          value={params.value ?? "ok"}
          label={t(`tachograph.statuses.${params.value ?? "ok"}`)}
        />
      ),
    });

    return [
      { field: "summary", headerName: t("tachograph.columns.summary"), minWidth: 260, flex: 1 },
      { field: "driver", headerName: t("tachograph.columns.driver"), minWidth: 240, flex: 1 },
      { field: "manufacturer", headerName: t("tachograph.columns.manufacturer"), width: 140 },
      statusColumn("status"),
      statusColumn("downloadStatus"),
      statusColumn("inspectionStatus"),
      statusColumn("companyCardStatus"),
      {
        field: "connectorStatus",
        headerName: t("tachograph.columns.connectorStatus"),
        width: 100,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<TachographRow, TachographConnectivity>) => (
          <TachographStatusIcon
            value={params.value ?? "online"}
            label={t(`tachograph.connectivity.${params.value ?? "online"}`)}
          />
        ),
      },
      { field: "legalLimit", headerName: t("tachograph.columns.legalLimit"), minWidth: 220, flex: 1 },
      {
        field: "nextDownloadUtc",
        headerName: t("tachograph.columns.nextDownloadUtc"),
        minWidth: 220,
        flex: 1,
      },
      {
        field: "calibrationExpiryDate",
        headerName: t("tachograph.columns.calibrationExpiryDate"),
        minWidth: 220,
        flex: 1,
      },
      { field: "updateTime", headerName: t("tachograph.columns.updateTime"), width: 170 },
    ];
  }, [t]);

  return (
    <I18nGrid
      rows={rows}
      columns={columns}
      loading={loading}
      density="compact"
      disableRowSelectionOnClick
      pageSizeOptions={TACHOGRAPH_PAGE_SIZES}
      initialState={{ sorting: { sortModel: [{ field: "updateTime", sort: "desc" }] } }}
    />
  );
}
